import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  formatScheduledPaymentDate,
  formatUserMessageText,
  getServiceOrderId,
  isOrderEditable,
  getOrderMutationBlockedMessage,
  OrderService,
  parseScheduledPaymentDateForInput,
  resolveOrderGraphqlUserMessage,
  ServiceOrder,
  shouldRefreshAfterOrderError
} from 'src/entities/order';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export interface EditScheduledPaymentDateDialogData {
  service: {
    order?: ServiceOrder;
  };
}

export interface EditScheduledPaymentDateDialogResult {
  success: boolean;
  shouldRefresh?: boolean;
  newDate?: Date | null;
  orderNumber?: string | number | null;
  order?: any;
}

type ViewState = 'form' | 'error';

@Component({
  selector: 'app-dialog-edit-fecha-pago-programada',
  templateUrl: './dialog-edit-fecha-pago-programada.component.html',
  styleUrls: ['./dialog-edit-fecha-pago-programada.component.css'],
  host: { class: 'edit-payment-date-host' },
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
  ],
})
export class DialogEditFechaPagoProgramadaComponent implements OnInit {
  state: ViewState = 'form';
  errorTitle: string | null = null;
  errorMessage: string | null = null;

  fechaPagoProgramadaInput = new FormControl<Date | null>(null, [
    Validators.required,
  ]);
  isLoading = false;
  private readonly orderId: string | null;

  constructor(
    public dialogRef: MatDialogRef<
      DialogEditFechaPagoProgramadaComponent,
      EditScheduledPaymentDateDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: EditScheduledPaymentDateDialogData,
    private readonly orderService: OrderService,
    private readonly toastr: ToastrService
  ) {
    this.orderId = getServiceOrderId(this.data.service);
  }

  ngOnInit(): void {
    const currentDate = this.data.service?.order?.scheduledPaymentDate;
    const parsedDate = parseScheduledPaymentDateForInput(currentDate);
    if (parsedDate) {
      this.fechaPagoProgramadaInput.setValue(parsedDate);
    }

    if (!this.canEdit) {
      this.fechaPagoProgramadaInput.disable();
    }
  }

  get canEdit(): boolean {
    return isOrderEditable(this.data.service);
  }

  get blockedMessage(): string {
    return getOrderMutationBlockedMessage(
      'editar',
      this.data.service?.order?.orderStatus
    );
  }

  updateFechaPagoProgramada(): void {
    if (this.isLoading || !this.canEdit) {
      return;
    }

    const dateValue = this.fechaPagoProgramadaInput.value;
    if (this.fechaPagoProgramadaInput.invalid || !dateValue) {
      this.toastr.warning('Debe ingresar una fecha válida en formato DD/MM/AAAA');
      return;
    }

    if (!this.orderId) {
      this.toastr.error('El servicio no tiene una OS asociada.');
      return;
    }

    this.isLoading = true;
    const dateToSend =
      dateValue instanceof Date ? dateValue : new Date(dateValue);

    this.orderService
      .updateOrderScheduledPaymentDate({
        orderId: this.orderId,
        scheduledPaymentDate: formatScheduledPaymentDate(dateToSend),
      })
      .subscribe({
        next: (result) => {
          this.isLoading = false;
          this.dialogRef.close({
            success: true,
            shouldRefresh: false,
            newDate: this.fechaPagoProgramadaInput.value,
            orderNumber:
              result.orderNumber ?? this.data.service?.order?.orderNumber,
            order: result
          });
        },
        error: (error: unknown) => {
          this.isLoading = false;
          const msg = resolveOrderGraphqlUserMessage(
            error,
            'No se pudo actualizar la fecha de pago programada.'
          );

          if (shouldRefreshAfterOrderError(error)) {
            this.errorTitle = msg.title;
            this.errorMessage = formatUserMessageText(msg);
            this.state = 'error';
            this.dialogRef.disableClose = true;
            return;
          }

          this.toastr.error(msg.title, formatUserMessageText(msg));
        },
      });
  }

  cerrarYActualizar(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close({
      success: false,
      shouldRefresh: true
    });
  }

  onCancel(): void {
    this.dialogRef.close({ success: false, shouldRefresh: false });
  }
}
